import React from 'react';
import NextLink from 'next/link';
import { LinkGroup, Link } from 'Styles/links';
import * as Styled from './NavDrawer.styled';
import { SlidingPanel } from '../../../SlidingPanel';
import ContentCard from '../../../ContentCard/ContentCard';
import ContainerQuery from '../../../ContainerQuery/ContainerQuery';
import capitalizeString from '../../../../util/capitalizeString';

/**
 * @param {Array} linksToRender array of links that contain subtitle and its sublinks
 * @returns a component that contains links that direct you to specific PCPs
 */
function renderLinks(linksToRender) {
  return (
    <Styled.PanelLinksContainer>
      {linksToRender.map((level3Section) => (
        <div key={level3Section.id}>
          {level3Section.text && (
            <Styled.Subtitle>{capitalizeString(level3Section.text ?? '')}</Styled.Subtitle>
          )}

          {level3Section.links && (
            <LinkGroup withoutTopPadding={!level3Section.text}>
              {level3Section.links.map((link) => (
                <Link
                  as={link.url ? NextLink : Link}
                  href={link.url}
                  color={link.color}
                  key={link.id}
                  $withArrow={link.withArrow}
                >
                  {capitalizeString(link.text ?? '')}
                </Link>
              ))}
            </LinkGroup>
          )}
        </div>
      ))}
    </Styled.PanelLinksContainer>
  );
}

/**
 * @param {Array} text string to convert to camel case
 * @returns the text converted to camel case
 * @example convertToCamelCase('hello world') => 'helloWorld'
 */
export function convertToCamelCase(text) {
  return text.charAt(0).toLowerCase() + text.slice(1).replace(/\s+/g, '');
}

/**
 * @param {Array} currentPanel array of objects containing next level's sections
 * @param {string} parentlabelName the data-label to concat to the prefix of the data-label
 * @returns a completed SlidingPanel component
 */
function buildSlidingPanel(currentPanel, parentlabelName) {
  const { sections, id, links, text, menuTileGroup, __typename } = currentPanel;

  /**
   * this check prevents this function from creating an inaccessible level3Section SlidingPanel.
   * level2Section has `sections`, so it will pass the condition in `flattenSections` and call this function.
   * if it does, it create an inaccessible SlidingPanel as there are no panel items that points to it
   */
  if (__typename === 'Level3Section') {
    return;
  }

  const dataLabelName = text && convertToCamelCase(text);
  return (
    <SlidingPanel data-label={`navDrawer${parentlabelName ? `_${parentlabelName}` : ''}_${dataLabelName}`} panelId={id} title={capitalizeString(text ?? '')} key={id}>
      {/**
        the check for level2Section is to prevent the function from render its `sections` as panel items.
        if we don't the check, PanelItemsContainer will render nothing but still add extra padding
        (this is because level2Sections do have `sections` but we want to render it as `links`)
       */}
      {sections && __typename !== 'Level2Section' && (
        <>
          <Styled.PanelItemsContainer>
            {sections.map(
              (panelInfo) =>
                panelInfo.sections && (
                  <Styled.PanelItem
                    forPanelId={panelInfo.id}
                    key={panelInfo.id}
                  >
                    {capitalizeString(panelInfo.text ?? '')}
                    <Styled.PanelSubtext>
                      {capitalizeString(panelInfo.subtext ?? '')}
                    </Styled.PanelSubtext>
                  </Styled.PanelItem>
                )
            )}
          </Styled.PanelItemsContainer>
          {links && <Styled.Divider />}
        </>
      )}

      {links && renderLinks(links)}

      {/**
        a level2Section has `sections` but no `links`,
        level2Section's `sections` are essentially link groups with or without subtitles,
        so we want to render them as links and not as panel items
       */}
      {__typename === 'Level2Section' && renderLinks(sections)}

      {menuTileGroup?.tiles && (
        <ContainerQuery>
          <Styled.Layout>
            {menuTileGroup.tiles.map((currentTile) => (
              <ContentCard
                backgroundColor="transparent"
                description={currentTile.text}
                header={currentTile.text}
                ctas={[{ url: currentTile.url }]}
                width={currentTile.image.file.details.image.width}
                height={currentTile.image.file.details.image.height}
                src={currentTile.image.file.url}
                titleFontSize={14}
                key={currentTile.id}
              />
            ))}
          </Styled.Layout>
        </ContainerQuery>
      )}
    </SlidingPanel>
  );
}

/**
 * Processes each node in the tree recursively and builds a SlidingPanel
 * @param {Array} sections Object to turn into a SlidingPanel
 * @param {string} parentlabelName the data-label of the parent section
 * @returns an array of SlidingPanels with no undefined elements
 */
export const flattenSections = (sections, parentlabelName) => {
  if (!sections) return [];

  const result = sections.reduce((acc, curr) => {
    const panel = buildSlidingPanel(curr, parentlabelName);

    if (!panel) {
      return acc;
    }
    const dataLabelName = curr.text && convertToCamelCase(curr.text);

    return [...acc, panel].concat(flattenSections(curr.sections, dataLabelName));
  }, []);

  return result;
};

export default {};
